import { noop } from "@omni/utils/function.utils";
import { omit, pick } from "@omni/utils/object.utils";
import { cn } from "@omni/utils/styles";
import { useRef } from "react";
import Transition from "react-transition-group/Transition";

import { FadeProps, TransitionPropTypeKeys } from "./fade.props";

const Fade = <T extends React.ElementType = "div">({
    ref,
    children,
    className,
    tag,
    baseClass = "fade",
    baseClassActive = "show",
    addEndListener = noop,
    ...otherProps
}: FadeProps<T>) => {
    const Tag = tag || ("div" as React.ElementType);
    const transitionProps = pick(otherProps, TransitionPropTypeKeys);
    const childProps = omit(otherProps, TransitionPropTypeKeys);

    const newRef = useRef(null);

    return (
        <Transition<React.ComponentPropsWithRef<typeof Tag>["ref"]>
            in={true}
            appear={true}
            enter={true}
            exit={true}
            timeout={150}
            mountOnEnter={false}
            unmountOnExit={false}
            onEnter={noop}
            onEntering={noop}
            onEntered={noop}
            onExit={noop}
            onExiting={noop}
            onExited={noop}
            addEndListener={addEndListener}
            {...transitionProps}
            nodeRef={ref ? ref : newRef}
        >
            {(status) => {
                const isActive = status === "entered";
                const classes = cn(
                    className,
                    baseClass,
                    isActive && baseClassActive
                );
                return (
                    <Tag
                        className={classes}
                        {...childProps}
                        ref={ref ? ref : newRef}
                    >
                        {children}
                    </Tag>
                );
            }}
        </Transition>
    );
};

export { Fade };
